import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CONTACT_INFO, SERVICES } from "../data";
import GalleryLightbox from "../components/GalleryLightbox";
import ContactForm from "../components/ContactForm";

export default function FugefrieGulve() {
  const serviceData = SERVICES.find((s) => s.slug === "fugefrie-gulve");
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fallback images if CMS fetch fails
  const fallbackImages = [
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/service-seamless.jpg",
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/seamless-gallery-1.jpg",
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/seamless-gallery-2.jpg"
  ];

  useEffect(() => {
    async function fetchGallery() {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/by-slug/da/fugefrie-gulve");
        if (res.ok) {
          const data = await res.json();
          setGallery(data);
        }
      } catch (err) {
        console.error("Fejl ved hentning af galleri fra CMS:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchGallery();
  }, []);

  const galleryImages = gallery?.images || fallbackImages;
  const galleryTitle = gallery?.title?.da || "Fugefrie gulve";
  const galleryDesc = gallery?.description?.da || serviceData.shortDesc;

  return (
    <>
      <Helmet>
        <title>Fugefrie Gulve & Polyurethan | Moderne Design & Komfort</title>
        <meta
          name="description"
          content="Elegante og fugefrie polyurethan-gulve (PU) i Halsnæs. Perfekt gangkomfort, lyddæmpning og moderne design til kontor, showroom og private boliger."
        />
        <meta property="og:title" content="Fugefrie Gulve & Polyurethan | Moderne Design & Komfort" />
        <meta
          property="og:description"
          content="Elegante og fugefrie polyurethan-gulve (PU) i Halsnæs. Perfekt gangkomfort, lyddæmpning og moderne design til kontor, showroom og private boliger."
        />
        <meta property="og:image" content={fallbackImages[0]} />
      </Helmet>

      {/* Page Header */}
      <section className="bg-brand-blue-900 text-white pt-48 pb-28 sm:pt-44 sm:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src={fallbackImages[0]}
            alt="Fugefrie gulve baggrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-blue-300 mb-3">
              Ydelser / Gulvløsninger
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
              Elegante Fugefrie Gulve
            </h1>
            <p className="text-lg text-brand-blue-100 mt-4 max-w-2xl">
              Moderne polyurethan-gulve (PU), der forener uovertruffen gangkomfort, lyddæmpning og et stilrent skandinavisk look.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content & Details */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Detailed Copy */}
            <div className="lg:col-span-7 space-y-8">
              <div className="prose prose-slate max-w-none">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-brand-blue-900 mb-4">
                  Hvad er et polyurethan-gulv (PU)?
                </h2>
                <p className="text-base text-concrete-600 leading-relaxed mb-6">
                  Et polyurethan-gulv (ofte kaldet PU-gulv) er en fugefri, flydende gulvbelægning baseret på polyurethan-plast. I modsætning til epoxy, som er ekstremt hårdt, er polyurethan blødere, mere elastisk og fleksibelt. Det giver nogle helt unikke fordele, når det kommer til komfort, akustik og design.
                </p>
                <p className="text-base text-concrete-600 leading-relaxed mb-6">
                  Hos Halsnæs Epoxygulve udfører vi fugefrie PU-gulve i højeste kvalitet. Vi leverer et fuldstændig ubrudt gulv, der strækker sig fra væg til væg uden samlinger eller overgange. Det giver et fantastisk minimalistisk udtryk, der passer perfekt til den moderne skandinaviske arkitektur.
                </p>

                <h3 className="text-xl font-bold text-brand-blue-900 mt-8 mb-4">
                  Fordele ved vores fugefrie PU-gulve
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {serviceData.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-2.5">
                      <i className="fa-solid fa-circle-check text-brand-blue-600 mt-1 text-sm"></i>
                      <span className="text-sm font-semibold text-concrete-700">{benefit}</span>
                    </div>
                  ))}
                </div>

                <h3 className="text-xl font-bold text-brand-blue-900 mt-8 mb-4">
                  Fantastisk gangkomfort og lyddæmpning
                </h3>
                <p className="text-base text-concrete-600 leading-relaxed mb-6">
                  Fordi polyurethan er elastisk, virker det stødabsorberende, når man går på det. Det skåner ryg og led, hvilket gør det særdeles velegnet til arbejdspladser, butikker og private hjem, hvor man står og går meget. Desuden dæmper materialet trinlyd markant, hvilket forbedrer rumakustikken væsentligt.
                </p>

                <h3 className="text-xl font-bold text-brand-blue-900 mt-8 mb-4">
                  Revneoverbyggende og slidstærkt
                </h3>
                <p className="text-base text-concrete-600 leading-relaxed mb-6">
                  Materialets elasticitet betyder også, at det er revneoverbyggende. Hvis der skulle opstå bittesmå svindrevner i det underliggende betonlag, vil PU-belægningen blot strække sig med og forblive fuldstændig intakt og fugefri på overfladen. Det sikrer en ekstremt lang levetid og et gulv, der altid ser ud som nyt.
                </p>
              </div>

              {/* Dynamic CMS Gallery Section */}
              <div className="pt-8 border-t border-concrete-100">
                <h3 className="text-2xl font-bold text-brand-blue-900 mb-2">
                  Galleri: {galleryTitle}
                </h3>
                <p className="text-sm text-concrete-500 mb-6">
                  {galleryDesc}
                </p>
                
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <i className="fa-solid fa-spinner animate-spin text-3xl text-brand-blue-600"></i>
                  </div>
                ) : (
                  <GalleryLightbox images={galleryImages} title={galleryTitle} />
                )}
              </div>
            </div>

            {/* Right: Sidebar with Contact Form */}
            <div className="lg:col-span-5 w-full lg:sticky lg:top-28">
              <ContactForm source="Ydelsesside: Fugefrie gulve" />
              
              {/* Quick Contact Box */}
              <div className="mt-6 rounded-2xl bg-concrete-50 p-6 border border-concrete-100 text-center space-y-4">
                <h4 className="font-bold text-brand-blue-900">Har du spørgsmål?</h4>
                <p className="text-xs text-concrete-500">
                  Kontakt Simon Babiel direkte for rådgivning om fugefrie polyurethan-gulve.
                </p>
                <div className="flex flex-col gap-2">
                  <a
                    href={`tel:+45${CONTACT_INFO.phone}`}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-white border border-concrete-200 py-2.5 text-sm font-bold text-brand-blue-900 hover:bg-concrete-50"
                  >
                    <i className="fa-solid fa-phone"></i>
                    Tlf: {CONTACT_INFO.phone}
                  </a>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-white border border-concrete-200 py-2.5 text-sm font-bold text-brand-blue-900 hover:bg-concrete-50"
                  >
                    <i className="fa-solid fa-envelope"></i>
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Bottom CTA Block */}
      <section className="relative text-white py-24 overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/service-seamless.jpg"
            alt="Fugefrie PU-gulve"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-brand-dark/60"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold uppercase tracking-tight mb-4">
            Vil du høre mere om vores fugefrie PU-gulve?
          </h2>
          <p className="text-base text-slate-200 max-w-2xl mx-auto mb-8">
            Vi rådgiver dig gerne om de mange designmuligheder, farver og glansgrader, så vi kan skabe det perfekte fugefrie gulv til dine lokaler.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center bg-brand-orange text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange/90 transition-all"
            >
              Få et tilbud
            </Link>
            <a
              href={`tel:+45${CONTACT_INFO.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-white/10 transition-all"
            >
              <i className="fa-solid fa-phone"></i>
              Ring til Simon
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
